import { useRef, useEffect, useState, useContext, memo } from 'react';
import UsageTrackerContext from 'automation-ui-usage-tracking/UsageTrackerContext';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { trackEvent } from 'automation-ui-usage-tracking/trackingUtils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIIconButton from 'UIComponents/button/UIIconButton';
import { CALYPSO } from 'HubStyleTokens/colors';
import styled from 'styled-components';
import { useDataPassingContext, DataPanelVisibleStates } from 'automation-ui-formula-editor/data-passing-panel/DataPassingContext';
import { DataPassingDispatchActionType } from 'automation-ui-formula-editor/data-passing-panel/constants/DataPassingActionConstants';
import { DATA_PANEL_CLASS_NAME } from 'automation-ui-formula-editor/data-passing-panel/utils/isClickEventFrom';
import { SIDE_PANEL_DATA_PANEL_WIDTH, SIDE_PANEL_DATA_PANEL_COLLAPSED_WIDTH } from 'automation-ui-core/constants/ui';
import { useHasScope } from 'automation-ui-core/hooks/authHooks';
import * as Scopes from 'automation-ui-formula-editor/constants/Scopes';
import DataPanelRevisedUXMode from 'automation-ui-formula-editor/data-passing-panel/DataPanelRevisedUXMode/DataPanelRevisedUXMode';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledDataPanelWrapper = styled.div.withConfig({
  displayName: "DataPassingPanel__StyledDataPanelWrapper",
  componentId: "rdgkwa-0"
})(["max-height:100%;display:flex;flex-direction:column !important;justify-content:flex-start !important;height:100%;", ""], ({
  panelWidth
}) => `width: ${panelWidth}px;`);
const CollapsedIconDiv = styled.div.withConfig({
  displayName: "DataPassingPanel__CollapsedIconDiv",
  componentId: "rdgkwa-1"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    borderBottom: `1px solid ${theme.color['border-core-default']}`,
    paddingInlineStart: `${theme.space[300]} !important`,
    paddingBlock: `${theme.space[25]} !important`
  };
});
const DataPassingPanel = () => {
  const [lastAddedEditedDataSource, setLastAddedEditedDataSource] = useState('');
  const [lastDeletedDataSource, setLastDeletedDataSource] = useState('');
  const [searchForDataToken, setSearchForDataToken] = useState();
  const {
    state: dataPassingState,
    dispatch: dataPassingDispatch
  } = useDataPassingContext();
  const {
    usageTracker
  } = useContext(UsageTrackerContext);
  const {
    actionConfigDataPanelDisplay,
    onChangeActionConfigDataPanelDisplay,
    isEmbedded,
    flowId,
    disableAddDataSource
  } = dataPassingState;
  const dataPanelRef = useRef(null);
  const addDataSourceEnabled = useHasScope(Scopes.WORKFLOWS_APP_ACCESS) && !disableAddDataSource;
  const isAddDataSourceBtnVisible = !isEmbedded && addDataSourceEnabled;
  const handleOnDataSourceCreated = scrollToDataSource => setLastAddedEditedDataSource(scrollToDataSource);
  const handleOnDataSourceDeleted = dataSourcePrefix => setLastDeletedDataSource(dataSourcePrefix);
  const handleSearchDataPanelForDataToken = searchForDataTokenArg => setSearchForDataToken(searchForDataTokenArg);
  useEffect(() => {
    //set our panel ref into context state so our visual indicator can use it.
    dataPassingDispatch({
      type: DataPassingDispatchActionType.HYDRATE_FROM_PANEL,
      payload: {
        dataPanelRef,
        fetchObjectCreatedCallback: handleOnDataSourceCreated,
        fetchObjectDeletedCallback: handleOnDataSourceDeleted,
        searchForDataTokenCallback: handleSearchDataPanelForDataToken
      }
    });
    trackEvent(usageTracker, Trackables.VIEWED_DATA_PASSING_PANEL, {
      screen: 'Data Passing Panel',
      flowId,
      source: 'Revised UX'
    });
    return function cleanup() {
      // Clean up our state to prep for the next initial state
      dataPassingDispatch({
        type: DataPassingDispatchActionType.DEHYDRATE_FROM_PANEL,
        payload: {}
      });
    };

    // Only run on initial mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleClosePanel = () => {
    trackEvent(usageTracker, Trackables.CLICK_DATA_PASSING_PANEL_COLLAPSE, {
      screen: 'Data Passing Panel'
    });
    onChangeActionConfigDataPanelDisplay(DataPanelVisibleStates.COLLAPSED);
  };
  const handleOpenPanel = () => onChangeActionConfigDataPanelDisplay(DataPanelVisibleStates.OPEN);

  // DEV NOTE: The className of this StyledDataPanelWrapper (data-passing-panel-container) is used for
  // onFocus/onBlur detection in the DataPassingContext. Do not remove. @pmerlino 12/7/22
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs(StyledDataPanelWrapper, {
      className: DATA_PANEL_CLASS_NAME,
      ref: dataPanelRef,
      "data-test-id": "data-panel",
      panelWidth: actionConfigDataPanelDisplay === DataPanelVisibleStates.OPEN ? SIDE_PANEL_DATA_PANEL_WIDTH : SIDE_PANEL_DATA_PANEL_COLLAPSED_WIDTH,
      children: [actionConfigDataPanelDisplay === DataPanelVisibleStates.COLLAPSED && /*#__PURE__*/_jsx(CollapsedIconDiv, {
        children: /*#__PURE__*/_jsx(UIIconButton, {
          size: "small",
          use: "unstyled",
          "data-test-id": "data-panel-expand-button",
          onClick: handleOpenPanel,
          tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-formula-editor.data-passing-panel.panel-collapse.tooltip"
          }),
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "last",
            size: "small",
            color: CALYPSO
          })
        })
      }), actionConfigDataPanelDisplay === DataPanelVisibleStates.OPEN && /*#__PURE__*/_jsx(DataPanelRevisedUXMode, {
        clearSearchForDataToken: () => setSearchForDataToken(undefined),
        isAddDataSourceBtnVisible: isAddDataSourceBtnVisible,
        lastAddedEditedDataSource: lastAddedEditedDataSource,
        lastDeletedDataSource: lastDeletedDataSource,
        onClosePanelClick: handleClosePanel,
        searchForDataToken: searchForDataToken,
        isEmbedded: isEmbedded
      })]
    })
  );
};

/**
 * DEV NOTE: React.memo HOC is used because DataPassingPanel doesn't take any props.
 * Without taking in props anytime it's parent re-renders react will try to re-render
 * the data panel. Memo avoids this by not allowing a re-mount / full re-render unless props
 * change. This works because no props === no change === no need to re-mount.
 */
export default /*#__PURE__*/memo(DataPassingPanel);